<template>
	<div class="w-full pointer-events-auto h-full">
		<div class="flex h-full w-full flex-col bg-white shadow-xl p-6">
			<div class="flex flex-col h-full">
				<div class="flex items-start justify-between mb-6">
					<h2
						id="slide-over-title"
						class="text-lg capitalize font-medium text-gray-900 py-0"
					>
						{{ $t("storeAvailability.title") }}
					</h2>
					<div class="ml-3 flex h-7 items-center">
						<button
							type="button"
							class="-m-2 p-2 text-gray-700"
							@click="emits('close')"
						>
							<span class="sr-only">{{ $t("Close panel") }}</span>
							<XMarkIcon class="w-6 h-6" />
						</button>
					</div>
				</div>
				<template v-if="!loading">
					<div
						v-for="store in storesRef"
						:key="store.Id"
						class="flex justify-between gap-4 mb-4 border-b border-graycustom pb-2 items-center"
					>
						<div class="flex flex-col flex-1 justify-center">
							<span>{{ store.Name }}</span>
							<NuxtLink
								:to="localePath(`/find-store?storeId=${store.Id}`)"
								class="text-xs text-gray-super-dark"
								@click="emits('close')"
								>{{ $t("storeAvailability.readMore") }}
							</NuxtLink>
						</div>
						<span v-if="hasStock(store) > 0" class="text-success">{{
							$t("storeAvailability.inStock")
						}}</span>
						<span v-else class="text-danger">{{
							$t("storeAvailability.outOfStock")
						}}</span>
					</div>
				</template>
				<template v-else>
					<div
						v-for="i in 4"
						:key="i"
						class="flex justify-between gap-4 mb-4 border-b border-graycustom pb-2 items-center"
					>
						<div class="flex flex-col flex-1 justify-center gap-1">
							<span
								class="flex animate-pulse flex-col items-top h-5 space-y-5 bg-gray-300 w-36"
							></span>
							<div
								class="flex animate-pulse flex-col items-top h-5 space-y-5 bg-gray-300 w-12"
							></div>
						</div>
						<div
							class="w-3/12 flex animate-pulse items-top h-5 bg-gray-300"
						></div>
					</div>
				</template>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { XMarkIcon } from "@heroicons/vue/24/solid";
import { onMounted, ref } from "vue";
import { storeToRefs } from "pinia";
import type { Store } from "@nwt/norce-pwa/dist/runtime/types/api/stores/stores";
import type { ProductOnHandByPartNo } from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import { activeStatusID } from "~/utils/constants";

const emits = defineEmits<{
	(e: "success"): void;
	(e: "close"): void;
}>();
const loading = ref(true);
const localePath = useLocalePath();

const { locale } = useI18n();

const { getProductOnHandByPartNo } = useNorcePwa();
const { selectedProduct } = storeToRefs(useProductStore());
const { stores } = storeToRefs(useWarehousesAndStores());

const mapStoresIds = (stores: Store[]) => {
	return stores.map((store) => ({ StoreId: store.Id }));
};
const stockStores = ref<ProductOnHandByPartNo>();
const storesRef = ref(stores.value[locale.value]);

const fetchStockFromStores = async () => {
	loading.value = true;
	if (stores.value[locale.value]?.length && selectedProduct.value) {
		const stocks = await getProductOnHandByPartNo(
			{
				cultureCode: useNorceStore().storeContext.cultureCode,
				partNo: selectedProduct.value.PartNo!,
				statusSeed: String(activeStatusID)
			},
			mapStoresIds(stores.value[locale.value]!)
		);
		stockStores.value = stocks;
	}
	loading.value = false;
};

const hasStock = (store: Store) =>
	stockStores.value?.Warehouses?.filter(
		(warehouse) => warehouse.StoreId === store.Id
	)[0]?.OnHand.Value ?? 0;

onMounted(() => {
	fetchStockFromStores();
});
</script>
